import { store as scrollStore } from "components/SmoothScroll/store";
import { WebGLSize, WebGLSyncData } from "components/WebGL/gl";
import { map } from "lodash";
import { store as interfaceStore } from 'store/interface';
import { Box3, Camera, Object3D, PerspectiveCamera, Vector3 } from 'three';
import { store as loaderStore } from "ui/Loader/store";
import { mainWebGLRenderer } from "./three";

/**
 * Return the unit size of the viewport (threejs unit)
 * @param distance Distance between the object3D position and the camera position
 * @param camera Camera of the Scene
 * @returns WebGLSize
 */
const calculateUnitSize = (distance:number, camera:PerspectiveCamera) : WebGLSize => {
  const vFov = camera.fov * (Math.PI / 180);
  const height = 2 * Math.tan(vFov / 2) * distance;
  const width = height * camera.aspect;

  return { width, height };
}

/**
 * Sync the object3D and an HTMLElement. Will write a WebGLSyncData object into the userData of the object3D
 * Available here: object3D.userData.sync<WebGLSyncData>
 * @param obj3d Object3D instance
 * @param el HTMLelement
 * @param camera Camera of the scene
 * @param resetSync If we need to reset the userData sync object. Needs this if we resize the viewport (bounds changed after that)
 */
const syncMeshToDOM = (obj3d : Object3D, el : HTMLElement, camera : PerspectiveCamera, resetSync = false) : void => {
  
  const unit = calculateUnitSize(camera.position.z - obj3d.position.z, camera);
  
  if(!obj3d.userData.sync || resetSync) {
    const top = el.offsetTop;
    const left = el.offsetLeft;
    const width = el.offsetWidth;
    const height = el.offsetHeight;

    obj3d.userData.sync = {
      scale: new Vector3(1,1,1),
      position: new Vector3(0,0,0),
      ref_bounds: { left, top}, 
      bounds: {width, height, left, top}, 
      progress: NaN,
    } as WebGLSyncData;
  }
  else{
    obj3d.userData.sync.bounds.top = obj3d.userData.sync.ref_bounds.top - scrollStore.getScroll();
    
    //basic progress
    const topProgress = (obj3d.userData.sync.bounds.top) / interfaceStore.viewport.webglHeight;
    const offsetProgress = (obj3d.userData.sync.bounds.height) / interfaceStore.viewport.webglHeight;
    obj3d.userData.sync.progress = topProgress + (offsetProgress/2);
  }
 
  const sx = obj3d.userData.sync.bounds.width / interfaceStore.viewport.width;
  const sy = obj3d.userData.sync.bounds.height / interfaceStore.viewport.webglHeight;
  obj3d.userData.sync.scale.set(unit.width * sx, unit.height * sy, obj3d.scale.z);

  let x = -(unit.width / 2) + (obj3d.userData.sync.scale.x / 2);
  x += (obj3d.userData.sync.bounds.left / interfaceStore.viewport.width) * unit.width;

  let y = (unit.height / 2) - (obj3d.userData.sync.scale.y / 2);
  y -= (obj3d.userData.sync.bounds.top / interfaceStore.viewport.webglHeight) * unit.height;

  obj3d.userData.sync.position.set(x,y,obj3d.position.z);
}

/**
 * Get the progression of the object3D inside the whole smooth scroll section.
 * @param obj3d Object3D instance
 * @returns Number Progress ratio
 */
const getScrollProgress = () : number =>  {
  const scrollWrapperEL = document.documentElement as HTMLElement;
  return scrollStore.getScroll() / (scrollWrapperEL.offsetHeight - interfaceStore.viewport.webglHeight);
}

/**
 * Get the 3d relative point to 2d coordinates
 * @param p 2d coordinates
 * @param viewport Screen Resolution
 * @return [x,y] axis normalized coordinates 
 */
const transform2DPointTo3D = (p : {x:number,y:number}, viewport:WebGLSize) : {x:number,y:number} =>  {
  return {
    x: (p.x / viewport.width) * 2 - 1,
    y: -(p.y / viewport.height) * 2 + 1
  }
}


//---- Spritesheet
/**
 * Preload all image from an image sequence
 * @param totalFrames Number instance
 * @param path WebGLSpritesheetPath object 
 */
const preloadImageSequence = (
  config:{total:number, startAt:number, requested:number},
  path : WebGLSpritesheetPath
) => {

  const isHighResVersion = interfaceStore.highResVersion;
  let folderImage = isHighResVersion ? "ktx2/" : "png/";
  
  let loader = loaderStore.getLoader("spritesheet");
  
  if(!loader) {
    loader = loaderStore.getLoader("texture");
    folderImage = "png/";
  }
  //custom filename
  path.url += folderImage;

  if(folderImage === "ktx2/") {
    path = {...path, files_extension: path.files_extension + ".ktx2"};
  }

  const imagesUrls = map(new Array(config.requested), (val, index) => {
    const filename = getImageFilename(config.total, config.startAt + index, path);
    return `${path.url}${filename}`;
  });

  return {loader, urls:imagesUrls};
}

/**
 * Preload all JSON Spritesheet file and load image texture associated
 * @param path WebGLSpritesheetPath object 
 */
// const preloadJSONSpritesheets = async (path : WebGLSpritesheetPath) => {
  // const output : { json : WebGLSpritesheetJSON[], textures : Texture[] } = {
  //   json: [],
  //   textures: []
  // };

  // await path.json?.reduce( async (previousPromise : Promise<any>, nextImage : any) => {
  //   await previousPromise;
  //   return (async (path:string) => {
  //     const jsonContent = await axios.get(path);
  //     const assetFolder = path.replace(/(\w|[-.])+$/, '');
      
  //     return new Promise<Texture>((resolve) => textureLoader.load(`${assetFolder}${jsonContent.data.meta.image}`, (texture) => {
  //       texture.colorSpace = SRGBColorSpace
  //       // texture.minFilter = NearestFilter;
  //       texture.generateMipmaps = false;
        
  //       // emitter.emit(EVENTS.SPRITESHEET_FRAME_LOADED, `${assetFolder}${jsonContent.data.meta.image}`);
        
  //       output.json.push({frames:jsonContent.data.frames, size:jsonContent.data.meta.size});
  //       output.textures.push(texture);

  //       return resolve(texture);
  //     }));
  //   })(nextImage)
  // }, Promise.resolve());

  // return output;
// }

/**
 * Get the right filename for an image sequence (000.png if > 100 frames), (00.png if > 10 and < 100 frames)
 * @param totalFrames Total frame number
 * @param frameNumber The frame number
 * @param path WebGLSpritesheetPath object 
 * @returns String
 */
const getImageFilename = (totalFrames:number, frameNumber : number, path : WebGLSpritesheetPath) : string => {
  const prefixNames = totalFrames > 100 ? '00' : '0';
  const pathUserPrefix = path.prefix_filename ?? '';

  let filename = `${pathUserPrefix}${frameNumber}.${path.files_extension}`;
    
  if(frameNumber < 10) {
    filename = `${pathUserPrefix}${prefixNames}${frameNumber}.${path.files_extension}`;
  }

  if(frameNumber >= 10 && frameNumber < 100) {
    filename = `${pathUserPrefix}${prefixNames.substring(1)}${frameNumber}.${path.files_extension}`;
  }

  return filename;
}

const getPixelSize = (object : Object3D, camera:Camera) => {
  // Get the bounding box of the hotspot in world coordinates
  const boundingBox = new Box3().setFromObject(object);
  // Get the min and max world coordinates
  const minWorld = boundingBox.min.clone();
  const maxWorld = boundingBox.max.clone();

  // Convert world coordinates to screen coordinates
  minWorld.project(camera);
  maxWorld.project(camera);

  // Calculate the screen width and height
  return {
    width: (Math.abs(maxWorld.x - minWorld.x) * mainWebGLRenderer!.domElement.offsetWidth) / 2,
    height: (Math.abs(maxWorld.y - minWorld.y) * mainWebGLRenderer!.domElement.offsetHeight) / 2
  }
}



export {
  calculateUnitSize, getImageFilename, getPixelSize, getScrollProgress,
  //spritesheet utils
  preloadImageSequence, syncMeshToDOM, transform2DPointTo3D
};

